import { z } from "zod";
import { useHistory } from "react-router-dom";
import { type TFunction, Trans, useTranslation } from "react-i18next";
import {
  IconChevronDown,
  IconChevronRight,
  IconChevronUp,
  IconHeadphones,
  IconLoader2,
  IconPresentationAnalytics,
} from "@tabler/icons-react";
import StarsIcon from "../../../assets/icons/stars.svg?react";
import { useCurrentUser } from "../../../domains/user/hooks/useCurrentUser";
import {
  seedArticleDetailCache,
  useArticleData,
} from "../../../features/article/hooks/useArticleData";
import { useArticleInteractions } from "../../../features/article/hooks/useArticleInteractions";
import { useArticleAnalytics } from "../../../features/article/hooks/useArticleAnalytics";
import { useArticlePageUIState } from "../../../features/article/hooks/useArticlePageUIState";
import { useDesktopArticleListening } from "../../../features/article/hooks/useDesktopArticleListening";
import {
  useNowReading,
  useNowReadingSurface,
} from "../../../features/article/hooks/useNowReading";
import { estimatedListenMinutes } from "../../../features/article/lib/speechSections";
import { useArticleSwipe } from "../../../features/article/hooks/useArticleSwipe";
import { sharedContentBackDestination } from "../../../domains/room/utils/roomContentPath";
import { getRoomName } from "../../../domains/room/hooks/useRoomName";
import { goBackInApp } from "../../../domains/app/utils/backNavigation";
import { useActiveLang } from "../../../domains/app/hooks/useActiveLang";
import { useAskAboutPaper } from "../../../domains/gpt/hooks/useAskAboutPaper";
import { useArticleChatGrounding } from "../../../domains/gpt/hooks/usePaperGrounding";
import { useApiQuery } from "../../../services/api";
import { ArticleSectionOffLabel } from "../../../features/article/components/sections/ArticleSectionOffLabel";
import { ArticleSectionWorksScore } from "../../../features/article/components/sections/ArticleSectionWorksScore";
import { ArticlePageChatBar } from "../../../features/article/components/ArticlePageChatBar";
import ScientificText from "../../../components/ScientificText";
import Loader from "../../../components/Loader";
import { DesktopArticlePageSkeleton } from "../../../components/skeleton/ArticlePageSkeleton";
import { DesktopTopNav } from "../../../components/app/desktopTopNav/DesktopTopNav";
import { DesktopBreadcrumb } from "../../../components/app/desktopTopNav/DesktopBreadcrumb";
import { SectionRail } from "../../../components/ui/SectionRail";
import { Tag } from "../../../components/ui/Tag";
import { Button } from "../../../components/ui/Button";
import { useArticleVisualAbstracts } from "../../../features/visual-abstract/useArticleVisualAbstracts";
import type { ArticlePageArticle } from "../../../features/article/hooks/useArticleData";
import { originalSourceUrl } from "../../../domains/article/utils/sourceUrl";
import { licenseLabel } from "../../../domains/article/utils/licenseLabel";
import { getItemTranslation } from "../../../domains/app/utils/getItemTranslation";
import {
  DesktopArticleHeader,
  ListenButton,
  type ArticleListenState,
} from "./DesktopArticleHeader";
import {
  articleFigures,
  FigureGallery,
  FigurePanel,
  FigureViewer,
} from "./ArticleFigures";
import { useArticleFigureView } from "./useArticleFigureView";
import { useArticlePageShell } from "./useArticlePageShell";
import { useArticlePagerKeys } from "./useArticlePagerKeys";
import { useLocationState } from "../../../domains/app/hooks/useLocationState";
import {
  congressBreadcrumb,
  useCongressAttributionFromNavigation,
} from "../../../domains/congress/congressAttribution";
import {
  exploreOriginState,
  exploreOriginStateSchema,
} from "../../../domains/article/utils/exploreOrigin";
import { collectionOriginStateSchema } from "../../../domains/article/utils/collectionOrigin";
import { RAIL_SECTION_ATTRIBUTE, useSectionRail } from "./useSectionRail";
import { readingMinutes } from "../../../domains/article/utils/readingMinutes";

// The blocks of the reading column, in the order the design gives them.
type SectionId =
  | "tldr"
  | "figures"
  | "background"
  | "objectives"
  | "methods"
  | "results"
  | "limitations"
  | "conclusion"
  | "abstract"
  | "declaration"
  | "additional-information"
  | "ask";

// The name each block reports in `article_section_read`: the phone's name
// where the phone shows the same block, so both surfaces read alike.
const ANALYTICS_SECTION = {
  tldr: "tldr",
  figures: "figures",
  background: "background",
  objectives: "objectives",
  methods: "methodology",
  results: "results",
  limitations: "limitations",
  conclusion: "conclusion",
  abstract: "abstract",
  declaration: "declaration",
  "additional-information": "keywords",
  ask: "ask",
} as const satisfies Record<SectionId, string>;

// The sections that are text to read, which the "min read" figure counts.
const READ_SECTIONS: ReadonlySet<SectionId> = new Set([
  "tldr",
  "background",
  "objectives",
  "methods",
  "results",
  "limitations",
  "conclusion",
  "abstract",
]);

/** One block of the reading column, and how the section rail names it. */
type ReadingSection = {
  id: SectionId;
  title: string;
  excerpt: string;
  content: React.ReactNode;
};

function nonEmpty(lines: string[]): string[] {
  return lines.filter((line) => line !== "");
}

// ── Section building blocks ─────────────────────────────────────────────

function SectionHeading({ children }: { children: string }): React.JSX.Element {
  return (
    <h2 className="m-0 text-desktop-heading font-medium text-ds-black">
      {children}
    </h2>
  );
}

function Prose({ text }: { text: string }): React.JSX.Element {
  return (
    <p className="m-0 text-desktop-body whitespace-pre-line text-ds-black">
      <ScientificText>{text}</ScientificText>
    </p>
  );
}

function Bullets({ items }: { items: string[] }): React.JSX.Element {
  return (
    <ul className="m-0 flex list-none flex-col gap-2.5 p-0 text-desktop-body">
      {items.map((item, index) => (
        <li key={index} className="flex gap-2.5">
          <span aria-hidden className="text-ds-primary">
            •
          </span>
          <span className="min-w-0 flex-1 text-ds-black">
            <ScientificText>{item}</ScientificText>
          </span>
        </li>
      ))}
    </ul>
  );
}

function MetaField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="m-0 text-footnote font-medium tracking-wider text-ds-slate-600 uppercase">
        {label}
      </h3>
      {children}
    </div>
  );
}

// Marks the section the audio is reading (Figma "Now reading"), on the page
// of the article that plays.
function NowReadingChip({
  articleId,
  sectionId,
}: {
  articleId: string;
  sectionId: SectionId;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const surface = useNowReadingSurface();
  if (
    surface === null ||
    surface.articleId !== articleId ||
    surface.sectionId !== sectionId
  ) {
    return null;
  }
  return (
    <span className="flex shrink-0 items-center gap-1 rounded-full bg-ds-moss-100 py-0.75 pr-2.5 pl-2 text-caption font-medium text-ds-moss-800">
      <IconHeadphones className="size-4" stroke={1.25} />
      {t("Now reading")}
    </span>
  );
}

// Follows the narration for the chip and the sidebar player. A leaf: it
// re-renders on every playback tick, and the page around it must not.
function NowReadingTracker({
  article,
  scrollRef,
}: {
  article: ArticlePageArticle;
  scrollRef: React.RefObject<HTMLElement | null>;
}): null {
  useNowReading(article, scrollRef);
  return null;
}

const roomListOriginSchema = z.object({
  roomList: z.string().nullable().default(null),
});

type PagerItem = ReturnType<typeof useArticleSwipe>["swipeList"][number];

/**
 * "2 of 3", then Previous and Next (Figma ".DesktopTopNav / Pager"). Each
 * button is off at its end. Hovering one shows the article it opens, and so
 * does a keyboard focus; the focus a click leaves on the button does not.
 */
function ArticleListPager({
  position,
  total,
  previous,
  next,
  onOpen,
}: {
  position: number;
  total: number;
  previous: PagerItem | null;
  next: PagerItem | null;
  onOpen: (offset: -1 | 1) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    // The previews hang from the pager, right-aligned with its last button.
    <div className="relative flex items-center gap-1.5">
      <span className="text-footnote text-ds-slate-600">
        {t("{{position}} of {{total}}", { position, total })}
      </span>
      <PagerButton
        label={t("Previous article")}
        direction={t("Previous")}
        target={previous}
        onOpen={() => {
          onOpen(-1);
        }}
      >
        <IconChevronUp stroke={1.5} />
      </PagerButton>
      <PagerButton
        label={t("Next article")}
        direction={t("Next")}
        target={next}
        onOpen={() => {
          onOpen(1);
        }}
      >
        <IconChevronDown stroke={1.5} />
      </PagerButton>
    </div>
  );
}

function PagerButton({
  label,
  direction,
  target,
  onOpen,
  children,
}: {
  label: string;
  /** "Previous" or "Next", above the title in the preview. */
  direction: string;
  /** The article the button opens, null at the end of the list. */
  target: PagerItem | null;
  onOpen: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <span className="group/pager flex">
      <button
        type="button"
        aria-label={label}
        disabled={target === null}
        onClick={onOpen}
        className="flex size-8 cursor-pointer items-center justify-center rounded-lg border border-ds-slate-100 bg-ds-white p-0 text-ds-slate-600 outline-none hover:border-ds-slate-600 focus-visible:shadow-focus-ring disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-ds-slate-100 [&>svg]:size-5"
      >
        {children}
      </button>
      {target === null ? null : (
        <span
          aria-hidden
          className="pointer-events-none absolute top-full right-0 z-20 mt-2 hidden w-80 flex-col gap-1 rounded-xl border border-ds-slate-100 bg-ds-white px-3.5 py-3 shadow-rail-card group-hover/pager:flex group-has-focus-visible/pager:flex"
        >
          <span className="flex min-w-0 gap-1 text-caption whitespace-nowrap text-ds-slate-600">
            <span>{direction}</span>
            {target.journal === null ? null : (
              <>
                <span>·</span>
                <Trans
                  i18nKey="In <name>{{name}}</name>"
                  values={{ name: target.journal.name }}
                  components={{
                    name: <span className="truncate text-ds-primary" />,
                  }}
                />
              </>
            )}
          </span>
          <span className="line-clamp-3 text-footnote font-semibold text-ds-black">
            {target.title}
          </span>
        </span>
      )}
    </span>
  );
}

// The Listen button of the header, on the desktop listening session. A leaf,
// for the same reason as NowReadingTracker.
function ArticleListenButton({
  article,
  list,
}: {
  article: ArticlePageArticle;
  /** The feed or collection the article was opened from. */
  list: ReturnType<typeof useArticleSwipe>["swipeList"];
}): React.JSX.Element {
  const listening = useDesktopArticleListening();
  const state = listening.articleListening(article.id);
  const listen = ((): ArticleListenState => {
    switch (state.state) {
      case "idle":
      case "ended":
        return { state: state.state, minutes: estimatedListenMinutes(article) };
      case "preparing":
      case "playing":
      case "paused":
        return state;
      default:
        throw new Error(state satisfies never);
    }
  })();
  return (
    <ListenButton
      listen={listen}
      onListen={() => {
        listening.listen(article, list);
      }}
    />
  );
}

/**
 * The visual abstract button of the header (Figma "Button · Visual abstract"):
 * it requests a poster on an eligible paper, shows the one being drawn, and
 * opens it once it is ready. It is absent while the eligibility is unknown and
 * on a paper the feature refuses: a dead offer costs more than none.
 */
function ArticleVisualAbstractButton({
  article,
}: {
  article: ArticlePageArticle;
}): React.JSX.Element | null {
  const { t } = useTranslation();
  const history = useHistory();
  // Not room-scoped, as on the phone's article page.
  const { list, request } = useArticleVisualAbstracts({
    articleId: article.id,
    articleTitle: article.title,
    roomId: null,
  });
  if (list === null) return null;
  const latest = list.artifacts.at(0);
  const offer = ((): VisualAbstractOffer | null => {
    if (latest !== undefined) {
      switch (latest.status) {
        case "ready":
          return { kind: "view", id: latest.id };
        case "queued":
        case "generating":
          return { kind: "generating" };
        case "failed":
          break;
        default:
          throw new Error(latest satisfies never);
      }
    }
    return list.eligibility.state === "ineligible"
      ? null
      : { kind: "generate" };
  })();
  if (offer === null) return null;

  switch (offer.kind) {
    case "generate":
      return (
        <Button
          variant="outline"
          size="sm"
          leftIcon={<IconPresentationAnalytics stroke={1.5} />}
          onClick={() => {
            void request();
          }}
        >
          {t("Visual abstract")}
        </Button>
      );
    case "generating":
      return (
        <Button
          variant="outline"
          size="sm"
          disabled
          leftIcon={<IconLoader2 stroke={1.5} className="animate-spin" />}
        >
          {t("Generating…")}
        </Button>
      );
    case "view":
      return (
        <Button
          variant="outline"
          size="sm"
          leftIcon={<IconPresentationAnalytics stroke={1.5} />}
          onClick={() => {
            history.push(`/visual-abstract/${offer.id}/preview`);
          }}
        >
          {t("View visual abstract")}
        </Button>
      );
    default:
      throw new Error(offer satisfies never);
  }
}

type VisualAbstractOffer =
  { kind: "generate" } | { kind: "generating" } | { kind: "view"; id: string };

function TextSection({
  title,
  aside,
  children,
}: {
  title: string;
  /** Beside the heading: the Now reading chip. */
  aside: React.ReactNode;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-3">
        <SectionHeading>{title}</SectionHeading>
        {aside}
      </div>
      {children}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────

/**
 * The article on a desktop (Figma "UX/UI Desktop Version", section "02 ·
 * Explore — Article"): one reading column with every section in one flow, the
 * section rail on the right edge, and the figures opening in a panel beside
 * the text or full screen.
 */
export function DesktopArticlePage({
  slug,
  roomId,
}: {
  slug: string;
  roomId: string | null;
}): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const { currentUser, isAuthenticated } = useCurrentUser();
  const { contentLang } = useActiveLang();
  const { exploreSearch, exploreList } = useLocationState(
    exploreOriginStateSchema
  );
  const { collectionOrigin } = useLocationState(collectionOriginStateSchema);
  // The room whose Discovery list opened this article, set by that list: the
  // pager walks the list only then, not on a list the swipe cache kept from
  // somewhere else.
  const { roomList } = useLocationState(roomListOriginSchema);
  const askAboutPaper = useAskAboutPaper();
  const { scrollRef } = useArticlePageShell(slug);

  const { article, articleError, articleImages } = useArticleData({
    slug,
    roomId,
  });

  const roomQuery = useApiQuery(
    "/webapp/private/rooms/:id",
    roomId !== null ? { param: { id: roomId } } : false
  );
  const currentRoom = roomQuery.state === "success" ? roomQuery.body : null;
  const congressOrigin = useCongressAttributionFromNavigation();

  const uiState = useArticlePageUIState({
    slug,
    article,
    roomIsPublic: currentRoom === null ? null : currentRoom.isPublic,
  });

  const { handleViewArticleSource, markConsumed, noteExit, noteTab } =
    useArticleAnalytics(
      article,
      uiState.isArticleReadMarkerOnScreen,
      currentRoom,
      scrollRef
    );

  const { shareArticle } = useArticleInteractions(article);
  const { swipeList } = useArticleSwipe(slug);

  const chatGrounding = useArticleChatGrounding(slug);

  const figures = uiState.isAllowedArticle
    ? articleFigures(articleImages, t)
    : [];
  const figureView = useArticleFigureView({ slug, count: figures.length });

  const showDigest =
    uiState.hasDigestContent &&
    uiState.isAllowedArticle &&
    uiState.canDisplayContent;
  const sections: ReadingSection[] =
    article === null
      ? []
      : readingSections({
          article,
          t,
          figures,
          showOverview: uiState.isAllowedArticle,
          showDigest,
          showAbstract: !showDigest && uiState.hasShowableAbstract,
          canAsk: chatGrounding !== null,
          openIndex:
            figureView.view.kind === "closed" ? null : figureView.view.index,
          onOpenFigure: (index) => {
            markConsumed();
            figureView.open(index);
          },
          onAsk: (question) => {
            noteExit("chat");
            askAboutPaper(article, question, "cwop_template");
          },
        });
  const rail = useSectionRail(
    scrollRef,
    sections.map((section) => section.id)
  );
  // Questions about a figure are written here, not taken from the paper's
  // template questions, so they are asked as typed. Offered only where the
  // page offers the chat at all.
  const askAboutFigure =
    article === null || chatGrounding === null
      ? null
      : (question: string): void => {
          noteExit("chat");
          askAboutPaper(article, question, "typed");
        };

  // Opened from a list, the bar pages through it: a room's Discovery list, or
  // the Explore feed. Previous and Next replace this article, so Back still
  // returns to the list.
  const listIndex = swipeList.findIndex((item) => item.slug === slug);
  const pagedList =
    listIndex === -1 || swipeList.length < 2
      ? null
      : exploreSearch !== null && exploreList
        ? ({ kind: "explore", search: exploreSearch } as const)
        : roomId !== null && roomList === roomId
          ? ({ kind: "room", roomId } as const)
          : null;
  function neighbour(offset: -1 | 1): PagerItem | null {
    const index = listIndex + offset;
    if (index < 0 || index >= swipeList.length) return null;
    const item = swipeList[index];
    if (item === undefined) {
      throw new Error(`should not happen: no list item at ${String(index)}`);
    }
    return item;
  }
  function openNeighbour(offset: -1 | 1): void {
    if (pagedList === null) {
      throw new Error("should not happen: paging without a list");
    }
    const target = neighbour(offset);
    // K before the first article and J after the last do nothing, as the
    // buttons, which are off there.
    if (target === null) return;
    seedArticleDetailCache(target, roomId);
    const path =
      roomId === null
        ? `/post/${target.slug}`
        : `/room/post/${roomId}/${target.slug}`;
    switch (pagedList.kind) {
      case "explore":
        history.replace(path, exploreOriginState(pagedList.search, true));
        return;
      case "room":
        history.replace(path, { roomList: pagedList.roomId });
        return;
      default:
        throw new Error(pagedList satisfies never);
    }
  }
  const pager =
    pagedList === null ? null : (
      <ArticleListPager
        position={listIndex + 1}
        total={swipeList.length}
        previous={neighbour(-1)}
        next={neighbour(1)}
        onOpen={openNeighbour}
      />
    );
  useArticlePagerKeys(pagedList === null ? null : openNeighbour);

  if (articleError !== null) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader expanded />
      </div>
    );
  }
  if (article === null) return <DesktopArticlePageSkeleton />;

  function handleBack() {
    noteExit("back");
    // A cold entry (a shared link, a notification) has nothing behind it, and
    // this button used to do nothing at all there. Same destination rule as the
    // mobile header and the playlist: the room only for someone who can open
    // it.
    goBackInApp(
      history,
      sharedContentBackDestination(
        isAuthenticated
          ? { kind: "signed-in", roomId, hasRoom: currentRoom !== null }
          : { kind: "signed-out" }
      )
    );
  }

  const journalName = article.journal === null ? null : article.journal.name;
  const license = article.license;
  const figureViewState = figureView.view;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <DesktopTopNav
        context={
          <DesktopBreadcrumb
            // From an Explore feed, Back returns to that feed, which the
            // first crumb names; any other way in, Back may land elsewhere.
            back={
              exploreSearch === null
                ? { kind: "separate", onBack: handleBack }
                : { kind: "first-crumb", onBack: handleBack }
            }
            // Opened from an Explore feed, Explore leads the trail and goes
            // back to that feed, room chip included. A room article reached
            // any other way runs through its room, which is a link, so the
            // nearest crumb returns to the room and not to its parent:
            // Rooms / [Room] / Article, Rooms being a sidebar destination of
            // its own. Opened from a congress space of this room, the space
            // follows the room.
            // Opened from a collection, the trail continues the collection's
            // own and ends on it.
            trail={
              collectionOrigin !== null
                ? [
                    ...collectionOrigin.trail,
                    {
                      label: collectionOrigin.collection.label,
                      to: {
                        pathname: collectionOrigin.collection.path,
                        state: {
                          congressAttribution:
                            collectionOrigin.collection.congressAttribution,
                        },
                      },
                    },
                  ]
                : exploreSearch !== null
                  ? [{ label: t("Explore"), to: `/discovery${exploreSearch}` }]
                  : currentRoom === null
                    ? [{ label: t("Explore"), to: "/discovery" }]
                    : [
                        { label: t("Rooms"), to: "/rooms" },
                        {
                          label: getRoomName(currentRoom, contentLang),
                          to: `/room/discovery/${currentRoom.id}`,
                        },
                        ...(congressOrigin !== null &&
                        congressOrigin.roomId === currentRoom.id
                          ? [congressBreadcrumb(congressOrigin)]
                          : []),
                      ]
            }
            current={
              collectionOrigin !== null
                ? t("Article")
                : currentRoom === null
                  ? t("For you")
                  : t("Article")
            }
          />
        }
        trailing={
          pager === null
            ? { kind: "search" }
            : { kind: "search-after", leading: pager }
        }
        surface="default"
        className={null}
      />

      <div className="flex min-h-0 flex-1">
        <div className="relative flex min-w-0 flex-1">
          <div
            ref={scrollRef}
            className="relative min-w-0 flex-1 overflow-y-auto px-8"
          >
            <div className="mx-auto flex max-w-desktop-reading flex-col gap-8 pt-8">
              <div className="flex flex-col gap-8">
                <DesktopArticleHeader
                  article={article}
                  readingMinutes={readingMinutes(
                    sections
                      .filter((section) => READ_SECTIONS.has(section.id))
                      .map((section) => section.excerpt)
                  )}
                  listenButton={
                    <ArticleListenButton article={article} list={swipeList} />
                  }
                  visualAbstractButton={
                    <ArticleVisualAbstractButton article={article} />
                  }
                  sourceUrl={originalSourceUrl(article)}
                  onViewSource={handleViewArticleSource}
                  onShare={
                    currentUser === null
                      ? null
                      : () => {
                          noteExit("share");
                          void shareArticle(article, currentUser);
                        }
                  }
                />
                {uiState.isAllowedArticle ? (
                  <ArticleSectionWorksScore
                    layout="desktop"
                    article={article}
                    sheetTarget={uiState.worksScoreSheetTarget}
                    onSheetTargetChange={uiState.setWorksScoreSheetTarget}
                  />
                ) : null}
                <ArticleSectionOffLabel article={article} />
              </div>

              <NowReadingTracker article={article} scrollRef={scrollRef} />
              {sections.map((section) => (
                <section
                  key={section.id}
                  {...{ [RAIL_SECTION_ATTRIBUTE]: section.id }}
                  data-gtm-section={ANALYTICS_SECTION[section.id]}
                  className="scroll-mt-8"
                >
                  {section.content}
                </section>
              ))}
              {/* A zero-height sentinel: it takes back both gaps around it,
                  so the composer's own 22px is all there is under the last
                  section, as drawn. */}
              <div ref={uiState.articleReadMarkerRef} className="-my-8" />

              <ArticlePageChatBar
                // One composer per article: the pager replaces the article
                // without leaving the page, and a question typed, or a
                // dictation running, belongs to the article it was for. The
                // remount releases the mic and drops a pending transcript.
                key={article.slug}
                article={article}
                // The phone hides the bar under its floating player. The
                // desktop plays in the sidebar, which leaves the bar free.
                isTrackPlaying={false}
                placement="docked"
                onOpenChat={() => {
                  noteExit("chat");
                }}
              />
            </div>
          </div>
          {figureViewState.kind === "closed" ? (
            // The rail hangs on the right edge of the 1032px reading block
            // (Figma "Article · single flow"), centred on the reading area.
            // It gives way to the figure panel, which takes that edge.
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center px-8">
              <div className="flex w-full max-w-desktop-block justify-end">
                <div className="pointer-events-auto">
                  <SectionRail
                    label={t("Sections")}
                    // A section joins the rail once it has been measured: its
                    // tick length is its height.
                    items={sections.flatMap((section) => {
                      const weight = rail.weights.get(section.id);
                      if (weight === undefined) return [];
                      return [
                        {
                          id: section.id,
                          title: section.title,
                          excerpt: section.excerpt,
                          weight,
                        },
                      ];
                    })}
                    activeId={rail.activeId}
                    onSelect={(id) => {
                      const selected = sections.find(
                        (section) => section.id === id
                      );
                      if (selected === undefined) {
                        throw new Error(
                          `should not happen: rail section "${id}" not shown`
                        );
                      }
                      noteTab(ANALYTICS_SECTION[selected.id]);
                      rail.scrollTo(id);
                    }}
                  />
                </div>
              </div>
            </div>
          ) : null}
        </div>

        {figureViewState.kind === "panel" ? (
          <FigurePanel
            figures={figures}
            index={figureViewState.index}
            onSelect={figureView.select}
            onEnlarge={figureView.enlarge}
            onClose={figureView.close}
            onAsk={askAboutFigure}
          />
        ) : null}
      </div>

      {figureViewState.kind === "viewer" ? (
        <FigureViewer
          figures={figures}
          index={figureViewState.index}
          journalName={journalName}
          license={license}
          licenseLabel={license === null ? null : licenseLabel(license)}
          onSelect={figureView.select}
          onClose={figureView.close}
          onAsk={askAboutFigure}
        />
      ) : null}
    </div>
  );
}

/**
 * The reading column, top to bottom, as the design orders it: what to
 * remember, the figures, the digest (or the abstract when there is no
 * digest), the declarations, the facts about the paper, and the questions to
 * ask it. A section with nothing to say is left out, and so is its tick on the
 * rail.
 */
function readingSections({
  article,
  t,
  figures,
  showOverview,
  showDigest,
  showAbstract,
  canAsk,
  openIndex,
  onOpenFigure,
  onAsk,
}: {
  article: ArticlePageArticle;
  t: TFunction;
  figures: ReturnType<typeof articleFigures>;
  showOverview: boolean;
  showDigest: boolean;
  showAbstract: boolean;
  canAsk: boolean;
  openIndex: number | null;
  onOpenFigure: (index: number) => void;
  onAsk: (question: string) => void;
}): ReadingSection[] {
  const sections: ReadingSection[] = [];

  const tldr = article.tldr;
  if (showOverview && tldr !== null && tldr !== "") {
    sections.push({
      id: "tldr",
      title: t("To remember"),
      excerpt: tldr,
      content: (
        <div className="flex flex-col gap-2 rounded-xl border border-ds-slate-100 bg-ds-moss-50 px-5 py-4 text-ds-primary">
          <span className="flex items-center gap-1 text-sm font-medium">
            <StarsIcon />
            {t("To remember")}
          </span>
          <p className="m-0 text-desktop-body">
            <ScientificText>{tldr}</ScientificText>
          </p>
        </div>
      ),
    });
  }

  if (figures.length > 0) {
    sections.push({
      id: "figures",
      title: t("Figures & tables"),
      excerpt: figures.map((figure) => figure.label).join(", "),
      content: (
        <div className="flex flex-col gap-4">
          <div className="flex items-baseline gap-2">
            <SectionHeading>{t("Figures & tables")}</SectionHeading>
            <span className="text-footnote text-ds-slate-600">
              {figures.length}
            </span>
          </div>
          <FigureGallery
            figures={figures}
            openIndex={openIndex}
            onOpen={onOpenFigure}
          />
        </div>
      ),
    });
  }

  if (showDigest) {
    const digest: {
      id: SectionId;
      title: string;
      text: string | string[];
    }[] = [
      {
        id: "background",
        title: t("Background"),
        text: article.background === null ? "" : article.background,
      },
      {
        id: "objectives",
        title: t("Objective"),
        text: nonEmpty(article.objectives),
      },
      {
        id: "methods",
        title: t("Methods"),
        text: article.methodology === null ? "" : article.methodology,
      },
      { id: "results", title: t("Results"), text: nonEmpty(article.results) },
      {
        id: "limitations",
        title: t("Limitations"),
        text: article.limitations === null ? "" : article.limitations,
      },
      {
        id: "conclusion",
        title: t("Conclusion"),
        text: nonEmpty(article.conclusion),
      },
    ];
    for (const entry of digest) {
      const text = entry.text;
      if (text.length === 0) continue;
      sections.push({
        id: entry.id,
        title: entry.title,
        excerpt: typeof text === "string" ? text : text.join(" "),
        content: (
          <TextSection
            title={entry.title}
            aside={
              <NowReadingChip articleId={article.id} sectionId={entry.id} />
            }
          >
            {typeof text === "string" ? (
              <Prose text={text} />
            ) : text.length === 1 && text[0] !== undefined ? (
              <Prose text={text[0]} />
            ) : (
              <Bullets items={text} />
            )}
          </TextSection>
        ),
      });
    }
  }

  const abstractText = article.abstractText;
  if (showAbstract && abstractText !== null && abstractText !== "") {
    sections.push({
      id: "abstract",
      title: t("Abstract"),
      excerpt: abstractText,
      content: (
        <TextSection title={t("Abstract")} aside={null}>
          <Prose text={abstractText} />
        </TextSection>
      ),
    });
  }

  const funding = article.funding;
  const disclosure = article.disclosure;
  const hasFunding = funding !== null && funding !== "";
  const hasDisclosure = disclosure !== null && disclosure !== "";
  if (hasFunding || hasDisclosure) {
    sections.push({
      id: "declaration",
      title: t("Declaration"),
      excerpt: [hasFunding ? funding : "", hasDisclosure ? disclosure : ""]
        .filter((text) => text !== "")
        .join(" "),
      content: (
        <div className="flex flex-col gap-5 border-t border-ds-slate-100 pt-8">
          <SectionHeading>{t("Declaration")}</SectionHeading>
          {hasFunding ? (
            <MetaField label={t("Funding")}>
              <Prose text={funding} />
            </MetaField>
          ) : null}
          {hasDisclosure ? (
            <MetaField label={t("Conflicts of interest")}>
              <Prose text={disclosure} />
            </MetaField>
          ) : null}
        </div>
      ),
    });
  }

  const keywords = nonEmpty(article.keywords);
  const topics = article.tags
    .map((tag) => ({ id: tag.id, name: getItemTranslation(tag) }))
    .filter((topic) => topic.name !== "");
  const license =
    article.license === null ? null : licenseLabel(article.license);
  if (topics.length > 0 || keywords.length > 0 || license !== null) {
    sections.push({
      id: "additional-information",
      title: t("Additional information"),
      excerpt: [...topics.map((topic) => topic.name), ...keywords].join(", "),
      content: (
        <div className="flex flex-col gap-5">
          <SectionHeading>{t("Additional information")}</SectionHeading>
          {topics.length > 0 ? (
            <MetaField label={t("Topics")}>
              <div className="flex flex-wrap gap-2">
                {topics.map((topic) => (
                  <Tag
                    key={topic.id}
                    size="sm"
                    variant="mint"
                    state="idle"
                    icon={null}
                    // Figma "LabelChip · Mint · topic": a shade lighter than
                    // the mint chip of the article type.
                    className="bg-ds-mint-50 text-ds-mint-600"
                  >
                    {topic.name}
                  </Tag>
                ))}
              </div>
            </MetaField>
          ) : null}
          {keywords.length > 0 ? (
            <MetaField label={t("Keywords")}>
              <div className="flex flex-wrap gap-2">
                {keywords.map((keyword, index) => (
                  <Tag
                    key={`${keyword}-${String(index)}`}
                    size="sm"
                    variant="neutral"
                    state="idle"
                    icon={null}
                  >
                    {keyword}
                  </Tag>
                ))}
              </div>
            </MetaField>
          ) : null}
          {license !== null ? (
            <MetaField label={t("License")}>
              <Prose text={license} />
            </MetaField>
          ) : null}
        </div>
      ),
    });
  }

  const questions = nonEmpty(article.templateQuestions);
  if (canAsk && questions.length > 0) {
    sections.push({
      id: "ask",
      title: t("Ask about this study"),
      excerpt: questions.join(" "),
      content: (
        <div className="flex flex-col">
          <SectionHeading>{t("Ask about this study")}</SectionHeading>
          <ul className="m-0 flex list-none flex-col p-0 pt-2">
            {questions.map((question) => (
              <li key={question} className="border-b border-ds-slate-100">
                <button
                  type="button"
                  onClick={() => {
                    onAsk(question);
                  }}
                  className="flex w-full cursor-pointer items-center gap-3 border-none bg-transparent px-0 py-3 text-left text-ui-body text-ds-black hover:text-ds-primary"
                >
                  <span className="min-w-0 flex-1">{question}</span>
                  <IconChevronRight
                    stroke={1.5}
                    className="size-5 shrink-0 text-ds-slate-600"
                  />
                </button>
              </li>
            ))}
          </ul>
        </div>
      ),
    });
  }

  return sections;
}
